import { useCustom, useUpdate } from '@refinedev/core';
import { Spin, Tag, Select } from 'antd';
import {
  X,
  Clock,
  User,
  AlertTriangle,
  Bug,
  Lightbulb,
  HelpCircle,
  Sparkles,
  ArrowUpRight,
} from 'lucide-react';
import { useState, type FC } from 'react';
import SentimentBadge from '@/components/feedback/SentimentBadge';
import TranscriptionPanel from '@/components/feedback/TranscriptionPanel';
import AudioPlayer from '@/components/feedback/AudioPlayer';
import AISuggestionBlock from '@/components/feedback/AISuggestionBlock';
import AIActionButtons from '@/components/feedback/AIActionButtons';

interface FeedbackDetail {
  id: string;
  title: string;
  type: string;
  status: string;
  priority: string;
  description: string | null;
  audioUrl: string | null;
  audioTranscript: string | null;
  screenshotUrl: string | null;
  analysis: { sentiment?: string; summary?: string; suggestions?: string[] } | null;
  tags: Array<{ id: string; name: string; slug: string; color: string }>;
  user: { id: string; email: string; firstName: string; lastName: string } | null;
  createdAt: string;
  analyzedAt: string | null;
}

interface FeedbackDetailPanelProps {
  feedbackId: string;
  onClose: () => void;
}

const STATUS_OPTIONS = [
  { value: 'new', label: 'Nouveau' },
  { value: 'analyzing', label: 'Analyse en cours' },
  { value: 'analyzed', label: 'Analysé' },
  { value: 'in_progress', label: 'En cours' },
  { value: 'resolved', label: 'Résolu' },
  { value: 'closed', label: 'Fermé' },
  { value: 'rejected', label: 'Rejeté' },
];

const TYPE_ICONS: Record<string, typeof Bug> = {
  bug: Bug,
  feature: Lightbulb,
  improvement: ArrowUpRight,
  question: HelpCircle,
  other: Sparkles,
};

const PRIORITY_COLORS: Record<string, string> = {
  critical: 'red',
  high: 'orange',
  medium: 'blue',
  low: 'default',
};

const FeedbackDetailPanel: FC<FeedbackDetailPanelProps> = ({ feedbackId, onClose }) => {
  const { data, isLoading } = useCustom<{ data: FeedbackDetail }>({
    url: `/api/v1/admin/feedbacks/${feedbackId}`,
    method: 'get',
    queryOptions: { enabled: !!feedbackId },
  });

  const { mutate: updateStatus } = useUpdate();

  const [aiSuggestion, setAiSuggestion] = useState<{ suggestedResponse: string; model: string; tokensUsed: number } | null>(null);

  const fb = data?.data?.data;

  const handleStatusChange = (newStatus: string) => {
    updateStatus({
      resource: `admin/feedbacks/${feedbackId}/status` as string,
      id: '',
      values: { status: newStatus },
      meta: { method: 'put' },
    });
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (!fb) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-slate-400 dark:text-slate-500">Feedback introuvable.</p>
      </div>
    );
  }

  const TypeIcon = TYPE_ICONS[fb.type] ?? Sparkles;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4 dark:border-slate-700">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <TypeIcon className="h-4 w-4 shrink-0 text-blue-500 dark:text-blue-400" />
            <h2 className="truncate text-sm font-bold text-slate-900 dark:text-white">
              {fb.title}
            </h2>
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <Tag color={PRIORITY_COLORS[fb.priority] ?? 'default'}>{fb.priority}</Tag>
            <SentimentBadge sentiment={fb.analysis?.sentiment} />
          </div>
        </div>
        <button
          onClick={onClose}
          className="ml-2 shrink-0 rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:text-slate-500 dark:hover:bg-slate-700 dark:hover:text-slate-300"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Content — scrollable */}
      <div className="flex-1 space-y-4 overflow-y-auto p-5">
        {/* Status selector */}
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Statut
          </span>
          <Select
            value={fb.status}
            onChange={handleStatusChange}
            options={STATUS_OPTIONS}
            size="small"
            className="w-40"
          />
        </div>

        {/* Meta */}
        <div className="grid grid-cols-2 gap-3">
          {fb.user && (
            <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
              <User className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500" />
              <span className="truncate">
                {fb.user.firstName} {fb.user.lastName}
              </span>
            </div>
          )}
          <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
            <Clock className="h-3.5 w-3.5" />
            <span>{new Date(fb.createdAt).toLocaleDateString('fr-FR')}</span>
          </div>
        </div>

        {/* Description */}
        {fb.description && (
          <div className="fc-card p-4">
            <p className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Description
            </p>
            <p className="mt-2 text-sm leading-relaxed text-slate-700 dark:text-slate-300">
              {fb.description}
            </p>
          </div>
        )}

        {/* AI Summary */}
        {fb.analysis?.summary && (
          <div className="fc-card border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-blue-500 dark:text-blue-400" />
              <span className="text-xs font-medium uppercase tracking-wider text-blue-600 dark:text-blue-400">
                Résumé IA
              </span>
            </div>
            <p className="mt-2 text-sm leading-relaxed text-blue-800 dark:text-blue-200">
              {fb.analysis.summary}
            </p>
          </div>
        )}

        {/* AI Suggestion Block */}
        {aiSuggestion && <AISuggestionBlock suggestion={aiSuggestion} />}

        {/* AI Action Buttons */}
        <AIActionButtons feedbackId={feedbackId} onSuggestionReceived={setAiSuggestion} />

        {/* Audio */}
        <AudioPlayer url={fb.audioUrl} />

        {/* Transcription */}
        <TranscriptionPanel transcript={fb.audioTranscript} />

        {/* Tags */}
        {fb.tags.length > 0 && (
          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Tags
            </p>
            <div className="flex flex-wrap gap-1.5">
              {fb.tags.map((tag) => (
                <Tag key={tag.id} color={tag.color || 'blue'}>
                  {tag.name}
                </Tag>
              ))}
            </div>
          </div>
        )}

        {/* Screenshot */}
        {fb.screenshotUrl && (
          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Capture d&apos;écran
            </p>
            <img
              src={fb.screenshotUrl}
              alt="Screenshot"
              className="w-full rounded-lg border border-slate-200 dark:border-slate-700"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackDetailPanel;
